/**
 * Which playlists a user should see in lists.
 *
 * Navidrome hands an admin every user's playlists, private ones included
 * (`playlistRepository.userFilter()` drops the owner filter for admins), on
 * both the native API and Subsonic `getPlaylists`. Feishin lists only the
 * viewer's own playlists and public ones, whatever the role.
 *
 * Kept free of imports so `node --test` can load it directly.
 */

export interface PlaylistViewer {
    userId?: null | string;
    username?: null | string;
}

export interface PlaylistVisibilityItem {
    owner?: null | string;
    ownerId?: null | string;
    public?: boolean | null;
}

export const isPlaylistVisibleTo = (
    playlist: PlaylistVisibilityItem,
    viewer: null | PlaylistViewer | undefined,
): boolean => {
    if (playlist.public) return true;

    // Minimal Subsonic replies omit owner/public: nothing to decide on.
    if (!playlist.owner && !playlist.ownerId) return true;

    const userId = viewer?.userId || '';
    const username = viewer?.username || '';
    // No viewer identity: the server's own filtering is all we have.
    if (!userId && !username) return true;

    if (userId && playlist.ownerId && playlist.ownerId === userId) return true;

    // Navidrome usernames are case-insensitive (`COLLATE NOCASE`).
    return Boolean(
        username && playlist.owner && playlist.owner.toLowerCase() === username.toLowerCase(),
    );
};

export const visiblePlaylists = <T extends PlaylistVisibilityItem>(
    playlists: T[],
    viewer: null | PlaylistViewer | undefined,
): T[] => playlists.filter((playlist) => isPlaylistVisibleTo(playlist, viewer));
